import { useEffect, useState } from "react";

/** Props Interface */
interface UseDebouncedProps<T> {
	value: T;
	fn?: (value: T) => void;
	timeout?: number;
}

export function useDebounced<T>({ value, fn, timeout = 250 }: UseDebouncedProps<T>) {
	/** State */
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	/** Effects */
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, timeout);

		return () => {
			clearTimeout(handler);
		};
	}, [value]);

	useEffect(() => {
		if (!fn) return;
		if (debouncedValue === value) {
			fn?.(value);
		}
	}, [debouncedValue, value, fn]);

	/** Computed */
	const isDebouncing = debouncedValue !== value;

	/** Return */
	return { debouncedValue, isDebouncing };
}
